// The app shell. Desktop: sidebar + content + insights rail. Mobile: a floating pill tab
// bar and a "Log a session" button. Design: HourSign Design System.dc.html section 06
// ("Two shells, one vocabulary") and the desktop/mobile shells in HourSign App.dc.html.
import * as React from "react";
import { useClerk } from "@clerk/react";
import { Link, NavLink, Outlet, useLocation, useParams } from "react-router";
import {
  ArrowLeft,
  Bell,
  CircleUser,
  HelpCircle,
  House,
  LogOut,
  Plus,
  Settings,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Body, BodySm, Mono } from "@/components/ui/typography";
import { OfflineBanner } from "@/components/states";
import { PageTransition } from "@/components/hoursign/PageTransition";
import { SkipLink } from "@/components/layout/SkipLink";
import { PublicFooter } from "@/components/layout/PublicFooter";
import { CommandPalette } from "@/components/hoursign/CommandPalette";
import { TrackSwitcher } from "@/components/hoursign/TrackSwitcher";
import { IconTile } from "@/components/hoursign/IconTile";
import { LiveRegion } from "@/lib/a11y";
import { modeFromPath, navFor, type NavItem } from "@/components/layout/nav";
import { backTarget } from "@/components/layout/back";
import { useMe } from "@/lib/me";
import { cn } from "@/lib/utils";

/* ---------------------------------------------------------------- insights slot ---- */

interface SlotStore {
  set: (node: React.ReactNode) => void;
  subscribe: (listener: () => void) => () => void;
  get: () => React.ReactNode;
}

// ponytail: a 15-line store instead of context state. Context state would re-render the
// page that provides the node, which would build a new node, which would set state again.
function createSlotStore(): SlotStore {
  let node: React.ReactNode = null;
  const listeners = new Set<() => void>();
  return {
    set(next) {
      node = next;
      for (const listener of listeners) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    get: () => node,
  };
}

// Only an admin's "view as user" session needs it, so it is not part of every first load.
const ViewAsBanner = React.lazy(() =>
  import("@/features/admin/components/ViewAsBanner").then((m) => ({ default: m.ViewAsBanner })),
);

const InsightsSlotContext = React.createContext<SlotStore | null>(null);

/**
 * A page hands the shell its insights rail:
 *
 *     useInsightsSlot(<InsightsPanel needsYou={…} pace={…} rules={…} />);
 *
 * The shell puts it in the right-hand rail from 1280px up, and above the page content
 * below that. Outside the shell (tests, public pages) the call does nothing.
 */
export function useInsightsSlot(node: React.ReactNode): void {
  const store = React.useContext(InsightsSlotContext);
  React.useEffect(() => {
    if (!store) return;
    store.set(node);
    return () => store.set(null);
  }, [store, node]);
}

function InsightsOutlet({ className }: { className?: string }) {
  const store = React.useContext(InsightsSlotContext);
  const empty = React.useCallback(() => null, []);
  const node = React.useSyncExternalStore(
    store?.subscribe ?? (() => () => undefined),
    store?.get ?? empty,
    empty,
  );
  if (!node) return null;
  return <div className={className}>{node}</div>;
}

/* ------------------------------------------------------------------------ shell ---- */

function useOnline(): boolean {
  const [online, setOnline] = React.useState(() => navigator.onLine);
  React.useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

// Design (HourSign App.dc.html, desktop shell): 11px gap, 11/12 padding, 14px radius,
// active = the primary block with its own shadow. Inactive sits on the sidebar surface.
function sidebarItemClasses(isActive: boolean): string {
  return cn(
    "flex h-11 items-center gap-[9px] rounded-[14px] py-0 pr-3 pl-[5px] text-label transition-colors",
    isActive
      ? "bg-sidebar-primary text-sidebar-primary-foreground font-semibold shadow-md"
      : "text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground font-medium",
  );
}

function UserMenu() {
  const clerk = useClerk();
  const me = useMe();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Account menu">
          <CircleUser aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <BodySm className="text-muted-foreground px-2 py-1.5">
          {me.data?.email ?? "Signed in"}
        </BodySm>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/account">
            <CircleUser aria-hidden="true" /> Account
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/settings">
            <Settings aria-hidden="true" /> Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/help">
            <HelpCircle aria-hidden="true" /> Help
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void clerk.signOut()}>
          <LogOut aria-hidden="true" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The amber dot that says "something here needs you". Never the only signal: the */
/** sidebar shows the count as a figure beside it. */
function AttentionDot() {
  return (
    <span
      aria-hidden="true"
      className="bg-warning-border border-card absolute top-2 right-2 size-2 rounded-full border-2"
    />
  );
}

function Sidebar({
  items,
  mode,
  dualRole,
}: {
  items: NavItem[];
  mode: "practitioner" | "supervisor";
  dualRole: boolean;
}) {
  const clerk = useClerk();
  const me = useMe();
  return (
    <aside
      className="border-sidebar-border bg-sidebar sticky top-0 hidden h-dvh w-(--sidebar-width) shrink-0 flex-col gap-1 border-r p-4 lg:flex"
      aria-label="Sidebar"
    >
      <Link to="/" className="mb-2 flex min-h-11 items-center gap-[10px] px-2 py-2">
        <span className="bg-sidebar-primary text-sidebar-primary-foreground text-body-sm flex size-[30px] shrink-0 items-center justify-center rounded-[10px] font-extrabold">
          H
        </span>
        <Body className="font-bold">HourSign</Body>
      </Link>
      <TrackSwitcher className="mb-2 w-full justify-between" />
      {dualRole ? (
        <div role="group" aria-label="Mode" className="mb-2 flex gap-1 px-1">
          <Button
            asChild
            size="sm"
            variant={mode === "practitioner" ? "default" : "outline"}
            className="flex-1"
          >
            <Link to="/">Practitioner</Link>
          </Button>
          <Button
            asChild
            size="sm"
            variant={mode === "supervisor" ? "default" : "outline"}
            className="flex-1"
          >
            <Link to="/supervise">Supervisor</Link>
          </Button>
        </div>
      ) : null}
      <nav aria-label="Main" className="flex flex-col gap-1">
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => sidebarItemClasses(isActive)}
          >
            <IconTile icon={item.icon} tone={item.tone} label={item.label} labelled />
            {item.label}
            {item.badge ? (
              <Mono className="bg-warning text-warning-foreground ml-auto rounded-full px-2 tabular-nums">
                {item.badge}
              </Mono>
            ) : null}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto flex flex-col gap-1">
        <span aria-hidden="true" className="bg-sidebar-border my-3 h-px" />
        <NavLink to="/account" className={({ isActive }) => sidebarItemClasses(isActive)}>
          <IconTile icon={CircleUser} tone="other" label="Account" labelled />
          Account
        </NavLink>
        <NavLink to="/settings" className={({ isActive }) => sidebarItemClasses(isActive)}>
          <IconTile icon={Settings} tone="individual" label="Settings" labelled />
          Settings
        </NavLink>
        <NavLink to="/help" className={({ isActive }) => sidebarItemClasses(isActive)}>
          <IconTile icon={HelpCircle} tone="info" label="Help" labelled />
          Help
        </NavLink>
        <div className="bg-muted mt-3 flex items-center gap-[10px] rounded-[16px] p-2">
          <span className="bg-sidebar-primary text-sidebar-primary-foreground text-caption flex size-[34px] shrink-0 items-center justify-center rounded-[11px] font-bold">
            <CircleUser aria-hidden="true" className="size-[19px]" />
          </span>
          <span className="text-caption text-muted-foreground min-w-0 flex-1 truncate">
            {me.data?.email ?? "Signed in"}
          </span>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Sign out"
            onClick={() => void clerk.signOut()}
          >
            <LogOut aria-hidden="true" />
          </Button>
        </div>
      </div>
    </aside>
  );
}

function TabBar({ items }: { items: NavItem[] }) {
  if (items.length === 0) return null;
  return (
    <nav
      aria-label="Main"
      className="bg-foreground fixed inset-x-[14px] bottom-[14px] z-40 flex items-center gap-1 rounded-full p-[7px] shadow-lg lg:hidden"
    >
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          aria-label={item.label}
          className={({ isActive }) =>
            cn(
              "relative flex h-11 items-center justify-center gap-[7px] rounded-full",
              isActive
                ? "bg-sidebar-primary text-sidebar-primary-foreground px-4 font-semibold"
                : "text-background/80 hover:text-background flex-1",
            )
          }
        >
          {({ isActive }) => (
            <>
              <IconTile
                icon={item.icon}
                tone={item.tone}
                variant={isActive ? "soft" : "solid"}
                label={item.label}
                labelled
              />
              {isActive ? <BodySm className="whitespace-nowrap">{item.label}</BodySm> : null}
              {!isActive && item.badge ? <AttentionDot /> : null}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

export function AppLayout() {
  const location = useLocation();
  const params = useParams();
  const me = useMe();
  const online = useOnline();
  const [slotStore] = React.useState(createSlotStore);
  // Where this practitioner came from, sent once for the acquisition report.
  React.useEffect(() => {
    void import("@/lib/acquisition").then((m) => m.sendAcquisitionOnce());
  }, []);

  const mode = modeFromPath(location.pathname);
  const items = navFor(mode, params.trackId);
  const tabs = items.filter((item) => item.onMobile);
  const dualRole = Boolean(me.data?.roles.practitioner && me.data.roles.supervisor);
  const logHref = params.trackId ? `/t/${params.trackId}/log/new` : null;

  // The home of the track you are in. Everything else gets a way back to it.
  const homeHref =
    mode === "supervisor" ? "/supervise" : params.trackId ? `/t/${params.trackId}` : "/";
  const atHome = location.pathname === homeHref;
  // One Back button, in the header, on every page that is not a home page.
  const back = backTarget(location.pathname, homeHref);

  return (
    <InsightsSlotContext.Provider value={slotStore}>
      <div className="bg-background min-h-dvh">
        <SkipLink />
        {me.data?.impersonation ? (
          <React.Suspense fallback={null}>
            <ViewAsBanner />
          </React.Suspense>
        ) : null}
        <OfflineBanner online={online} />
        <CommandPalette />
        <div className="flex">
          <Sidebar items={items} mode={mode} dualRole={dualRole} />

          <div className="flex min-h-dvh min-w-0 flex-1 flex-col">
            <header className="border-border flex h-(--header-height) items-center justify-between gap-2 border-b px-4 sm:px-6 lg:px-8">
              {back ? (
                // min-w-0 + truncate: a long label ("Back to supervisors & sites") must give way
                // to the three icon buttons on a phone, not push them off the screen.
                <Button asChild variant="ghost" className="min-w-0 shrink">
                  <Link to={back.href}>
                    <ArrowLeft aria-hidden="true" />
                    <span className="truncate">{back.label}</span>
                  </Link>
                </Button>
              ) : (
                <>
                  <Body className="truncate font-bold lg:hidden">HourSign</Body>
                  <span className="hidden lg:block" />
                </>
              )}
              <div className="flex shrink-0 items-center gap-1">
                {!atHome ? (
                  <Button
                    asChild
                    variant="ghost"
                    size="icon"
                    aria-label="Home"
                    className="lg:hidden"
                  >
                    <Link to={homeHref}>
                      <House aria-hidden="true" />
                    </Link>
                  </Button>
                ) : null}
                {dualRole ? (
                  <Button asChild variant="ghost" size="sm" className="lg:hidden">
                    <Link to={mode === "supervisor" ? "/" : "/supervise"}>
                      <Users aria-hidden="true" />
                      {mode === "supervisor" ? "Practitioner" : "Supervisor"}
                    </Link>
                  </Button>
                ) : null}
                <Button asChild variant="ghost" size="icon" aria-label="Notifications">
                  <Link to="/notifications">
                    <Bell aria-hidden="true" />
                  </Link>
                </Button>
                <UserMenu />
              </div>
            </header>

            <div className="mx-auto flex w-full max-w-(--content-max) flex-wrap items-start gap-6 px-4 py-6 sm:px-6 lg:px-8">
              <main
                id="main"
                className="flex flex-col gap-4"
                // The rail drops below the content instead of crushing it.
                style={{ flex: "1 1 520px", minWidth: "min(520px, 100%)" }}
              >
                <PageTransition>
                  <Outlet />
                </PageTransition>
                {/* Below xl the rail follows the page so the title comes first on a phone. */}
                <InsightsOutlet className="xl:hidden" />
              </main>
              <InsightsOutlet className="hidden w-(--insights-width) shrink-0 xl:block" />
              <LiveRegion />
            </div>

            {/* Clears the floating pill tab bar on mobile: bar height + 28px. */}
            <div className="mt-auto pb-[calc(var(--bottom-nav-height)+28px+env(safe-area-inset-bottom))] lg:pb-0">
              <PublicFooter />
            </div>
          </div>
        </div>

        {logHref && mode === "practitioner" ? (
          <Button
            asChild
            size="lg"
            className="fixed right-[14px] bottom-[calc(var(--bottom-nav-height)+22px)] z-40 h-12 rounded-full shadow-lg lg:hidden"
          >
            <Link to={logHref}>
              <Plus aria-hidden="true" />
              Log a session
            </Link>
          </Button>
        ) : null}

        <TabBar items={tabs} />
      </div>
    </InsightsSlotContext.Provider>
  );
}
